import React, { useState } from "react";
import { Youtube, ChevronUp, ChevronDown, Eye, Bookmark, GitCompare, Check } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { DiscoveryInfluencer } from "../../types/discovery.types";

type SortField = "name" | "followers" | "avgLikes" | "avgViews" | "engagement" | "reach";
type SortDirection = "asc" | "desc";

export interface InfluencerListViewProps {
  influencers: DiscoveryInfluencer[];
  onInfluencerClick?: (influencer: DiscoveryInfluencer) => void;
  isSelected: (id: string) => boolean;
  onToggleCompare: (influencer: DiscoveryInfluencer) => void;
  canAddMore: boolean;
}

export function InfluencerListView({ 
  influencers, 
  onInfluencerClick,
  isSelected,
  onToggleCompare,
  canAddMore
}: InfluencerListViewProps) {
  const [sortField, setSortField] = useState<SortField>("followers");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const parseFollowerCount = (str: string): number => {
    if (!str) return 0;
    const num = parseFloat(str.replace(/[^0-9.]/g, ""));
    if (str.includes("M")) return num * 1000000;
    if (str.includes("K")) return num * 1000;
    return num;
  };

  const getNumericValue = (inf: DiscoveryInfluencer, field: SortField): number => {
    switch (field) {
      case "followers":
        return parseFollowerCount(inf.followers?.instagram || inf.followers?.youtube || "0");
      case "avgLikes":
        return parseFollowerCount(inf.avgLikes || "0");
      case "avgViews":
        return parseFollowerCount(inf.avgViews || "0");
      case "engagement":
        return parseFloat(inf.engagement?.replace("%", "") || "0");
      case "reach":
        return parseFollowerCount(inf.estimatedReach || "0");
      default:
        return 0;
    }
  };

  const sortedInfluencers = [...influencers].sort((a, b) => {
    if (sortField === "name") {
      return sortDirection === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    const aVal = getNumericValue(a, sortField);
    const bVal = getNumericValue(b, sortField);
    return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
  });

  const getEngagementColor = (engagement: string | undefined) => {
    if (!engagement) return "text-muted-foreground";
    const value = parseFloat(engagement.replace("%", ""));
    if (value >= 5) return "text-green-600 font-semibold";
    if (value >= 2) return "text-amber-600 font-medium";
    return "text-red-500";
  };

  const getTierBadgeClass = (tier: string | undefined) => {
    switch (tier?.toLowerCase()) {
      case "mega":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "macro":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "micro":
        return "bg-green-100 text-green-800 border-green-200";
      case "nano":
        return "bg-gray-100 text-gray-700 border-gray-200";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };

  const getInitials = (name: string) => {
    return name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
  };

  const SortableHeader = ({ field, children, className }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <TableHead
      className={cn("cursor-pointer select-none hover:bg-muted/50 transition-colors", className)}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortField === field && (
          sortDirection === "asc" ? (
            <ChevronUp className="w-3.5 h-3.5 text-primary" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-primary" />
          )
        )}
      </div>
    </TableHead>
  );

  // Empty state
  if (influencers.length === 0) {
    return (
      <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
        <p className="text-foreground font-medium mb-1">No creators found</p>
        <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden select-none">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-muted/50 z-10">
            <TableRow className="hover:bg-transparent">
              <SortableHeader field="name" className="min-w-[200px]">Creator</SortableHeader>
              <SortableHeader field="followers" className="text-right min-w-[100px]">Followers</SortableHeader>
              <SortableHeader field="avgLikes" className="text-right min-w-[90px] hidden lg:table-cell">Avg Likes</SortableHeader>
              <SortableHeader field="avgViews" className="text-right min-w-[90px] hidden lg:table-cell">Avg Views</SortableHeader>
              <SortableHeader field="engagement" className="text-right min-w-[100px]">Engagement</SortableHeader>
              <SortableHeader field="reach" className="text-right min-w-[90px] hidden xl:table-cell">Reach</SortableHeader>
              <TableHead className="min-w-[100px] hidden md:table-cell">Category</TableHead>
              <TableHead className="min-w-[80px]">Tier</TableHead>
              <TableHead className="text-right min-w-[130px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedInfluencers.map((inf, index) => {
              const selected = isSelected(inf.id);
              return (
                <TableRow
                  key={inf.id}
                  className={cn(
                    "cursor-pointer transition-colors",
                    index % 2 === 0 ? "bg-background" : "bg-muted/20",
                    "hover:bg-accent/50",
                    selected && "bg-purple-50 hover:bg-purple-100 border-l-4 border-l-purple-400"
                  )}
                  onClick={() => onInfluencerClick?.(inf)}
                >
                  {/* Avatar + Name */}
                  <TableCell className="py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 flex-shrink-0">
                        <AvatarImage src={inf.profilePic || undefined} alt={inf.name} className="object-cover" />
                        <AvatarFallback className="bg-primary/10 text-primary font-semibold text-sm">
                          {getInitials(inf.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-foreground truncate">{inf.name}</span>
                          {inf.verified && (
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-bold flex-shrink-0">✓</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>

                  {/* Followers */}
                  <TableCell className="text-right py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {inf.platforms.includes("instagram") && inf.followers?.instagram && (
                        <span className="flex items-center gap-1 text-sm font-medium">
                          <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-3.5 h-3.5" />
                          {inf.followers.instagram}
                        </span>
                      )}
                      {inf.platforms.includes("youtube") && inf.followers?.youtube && !inf.followers?.instagram && (
                        <span className="flex items-center gap-1 text-sm font-medium">
                          <Youtube className="w-3.5 h-3.5 text-red-500" />
                          {inf.followers.youtube}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* Avg Likes */}
                  <TableCell className="text-right py-3 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground">{inf.avgLikes || "—"}</span>
                  </TableCell>

                  {/* Avg Views */}
                  <TableCell className="text-right py-3 hidden lg:table-cell">
                    <span className="text-sm text-muted-foreground">{inf.avgViews || "—"}</span>
                  </TableCell>

                  {/* Engagement */}
                  <TableCell className="text-right py-3">
                    <span className={cn("text-sm", getEngagementColor(inf.engagement))}>
                      {inf.engagement || "—"}
                    </span>
                  </TableCell>

                  {/* Reach */}
                  <TableCell className="text-right py-3 hidden xl:table-cell">
                    <span className="text-sm text-muted-foreground">{inf.estimatedReach || "—"}</span>
                  </TableCell>

                  {/* Category */}
                  <TableCell className="py-3 hidden md:table-cell">
                    {inf.categories?.[0] ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700 border border-blue-100 truncate max-w-[100px]">
                        {inf.categories[0]}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>

                  {/* Tier */}
                  <TableCell className="py-3">
                    {inf.tier && (
                      <span className={cn(
                        "inline-flex px-2 py-0.5 text-xs rounded-full border font-medium capitalize",
                        getTierBadgeClass(inf.tier)
                      )}>
                        {inf.tier}
                      </span>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-right py-3">
                    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button 
                              size="sm" 
                              variant={selected ? "secondary" : "ghost"} 
                              className={cn(
                                "h-8 w-8 p-0",
                                selected && "bg-purple-100 text-purple-700 hover:bg-purple-200"
                              )}
                              title={selected ? "Remove from compare" : "Compare"}
                              onClick={() => onToggleCompare(inf)}
                              disabled={!selected && !canAddMore}
                              type="button"
                            >
                              {selected ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <GitCompare className="w-4 h-4" />
                              )}
                            </Button>
                          </span>
                        </TooltipTrigger>
                        {!selected && !canAddMore && (
                          <TooltipContent>You can compare up to 4 influencers</TooltipContent>
                        )}
                      </Tooltip>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Save" type="button">
                        <Bookmark className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="default" 
                        className="h-8 px-3" 
                        title="View"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onInfluencerClick?.(inf);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Mobile stacked view */}
      <div className="md:hidden">
        {sortedInfluencers.map((inf, index) => {
          const selected = isSelected(inf.id);
          return (
            <div
              key={`mobile-${inf.id}`}
              className={cn(
                "p-4 border-t border-border cursor-pointer transition-colors",
                index % 2 === 0 ? "bg-background" : "bg-muted/20",
                "hover:bg-accent/50",
                selected && "bg-purple-50 border-l-4 border-l-purple-400"
              )}
              onClick={() => onInfluencerClick?.(inf)}
            >
              <div className="flex items-start gap-3">
                {/* Compare toggle for mobile */}
                <div onClick={(e) => e.stopPropagation()}>
                  <Button
                    size="sm"
                    variant={selected ? "secondary" : "ghost"}
                    className={cn(
                      "h-8 w-8 p-0",
                      selected && "bg-purple-100 text-purple-700 hover:bg-purple-200"
                    )}
                    onClick={() => onToggleCompare(inf)}
                    disabled={!selected && !canAddMore}
                    type="button"
                  >
                    {selected ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <GitCompare className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <Avatar className="w-12 h-12 flex-shrink-0">
                  <AvatarImage src={inf.profilePic || undefined} alt={inf.name} className="object-cover" />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                    {getInitials(inf.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="font-semibold text-foreground truncate">{inf.name}</span>
                    {inf.verified && (
                      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-500 text-white text-[9px] font-bold">✓</span>
                    )}
                    {inf.tier && (
                      <span className={cn(
                        "inline-flex px-1.5 py-0.5 text-[10px] rounded-full border font-medium capitalize ml-auto",
                        getTierBadgeClass(inf.tier)
                      )}>
                        {inf.tier}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    {inf.followers?.instagram && (
                      <span className="flex items-center gap-1">
                        <img src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png" alt="Instagram" className="w-3 h-3" />
                        {inf.followers.instagram}
                      </span>
                    )}
                    <span className={cn(getEngagementColor(inf.engagement))}>
                      {inf.engagement}
                    </span>
                    {inf.avgLikes && <span className="text-muted-foreground">{inf.avgLikes} likes</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
